import { useMemo } from 'react';
import { cn } from '@/shared/lib/utils';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type AvatarShape = 'circle' | 'rounded';

export interface AvatarProps {
  name: string;
  size?: AvatarSize;
  shape?: AvatarShape;
  className?: string;
}

const AVATAR_COLORS = [
  { bg: 'bg-[var(--avatar-0-bg)]', text: 'text-[var(--avatar-0-text)]' },
  { bg: 'bg-[var(--avatar-1-bg)]', text: 'text-[var(--avatar-1-text)]' },
  { bg: 'bg-[var(--avatar-2-bg)]', text: 'text-[var(--avatar-2-text)]' },
  { bg: 'bg-[var(--avatar-3-bg)]', text: 'text-[var(--avatar-3-text)]' },
  { bg: 'bg-[var(--avatar-4-bg)]', text: 'text-[var(--avatar-4-text)]' },
  { bg: 'bg-[var(--avatar-5-bg)]', text: 'text-[var(--avatar-5-text)]' },
  { bg: 'bg-[var(--avatar-6-bg)]', text: 'text-[var(--avatar-6-text)]' },
  { bg: 'bg-[var(--avatar-7-bg)]', text: 'text-[var(--avatar-7-text)]' },
];

const DEFAULT_AVATAR_COLOR = AVATAR_COLORS[0]!;

const SIZE_STYLES: Record<AvatarSize, { container: string; text: string }> = {
  xs: { container: 'w-7 h-7', text: 'text-[10px] font-bold' },
  sm: { container: 'w-9 h-9', text: 'text-xs font-semibold' },
  md: { container: 'w-10 h-10', text: 'text-sm font-semibold' },
  lg: { container: 'w-12 h-12', text: 'text-base font-bold' },
  xl: { container: 'w-24 h-24', text: 'text-3xl font-bold' },
};

const SHAPE_STYLES: Record<AvatarShape, string> = {
  circle: 'rounded-full',
  rounded: 'rounded-xl',
};

function djb2Hash(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash);
}

function getInitials(name: string): string {
  return name
    .trim()
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('') || '?';
}

export function Avatar({
  name,
  size = 'md',
  shape = 'circle',
  className,
}: AvatarProps): JSX.Element {
  const color = useMemo(() => {
    const index = djb2Hash(name) % AVATAR_COLORS.length;
    return AVATAR_COLORS[index] ?? DEFAULT_AVATAR_COLOR;
  }, [name]);

  const initials = useMemo(() => getInitials(name), [name]);
  const sizeStyles = SIZE_STYLES[size];
  const shapeClass = shape === 'circle' ? 'rounded-full' : (size === 'xl' ? 'rounded-2xl' : SHAPE_STYLES[shape]);

  return (
    <div
      className={cn(
        'flex items-center justify-center flex-none select-none',
        sizeStyles.container,
        sizeStyles.text,
        shapeClass,
        color.bg,
        color.text,
        className,
      )}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}
